import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { PurposeCodePickerProps, PurposeCodeOption } from './types';
import MultiSelectPicker from 'components/MultiSelectPicker';
import { customComponentInternalProps } from 'components/FormCreatorComponent/types';

/**
 * Format purpose code for display: {code} - {country_name} - {currencies_code}
 */
const formatPurposeCode = (option: PurposeCodeOption): string => {
	const parts = [option.code];
	if (option.countryName) parts.push(option.countryName);
	if (option.currenciesCode) parts.push(option.currenciesCode);
	return parts.join(' - ');
};

/**
 * Purpose Code Picker Component
 * Multi-select dropdown for selecting payment purpose codes.
 * Uses the generic MultiSelectPicker with purpose code-specific formatting.
 */
export default function PurposeCodePicker(props: PurposeCodePickerProps) {
	const {
		value,
		onChange,
		error,
		helperText,
		disabled,
		label,
		hideTitle = false,
		required = false,
		options = [],
		loading = false,
		noOptions = false,
	} = props;

	const t = useAppSelector(selectTranslations);

	return (
		<MultiSelectPicker<number, PurposeCodeOption>
			value={value}
			onChange={onChange}
			options={options}
			getOptionValue={(opt) => opt.id}
			renderOptionLabel={formatPurposeCode}
			label={label || t('page.payment_categories.field.purpose_codes')}
			title={t('page.payment_categories.field.purpose_codes_title')}
			hideTitle={hideTitle}
			required={required}
			disabled={disabled}
			loading={loading}
			noOptions={noOptions}
			noOptionsText={t('page.payment_categories.field.purpose_codes_empty')}
			error={error}
			helperText={helperText}
			direction="ltr"
		/>
	);
}

/**
 * Form-compatible wrapper for PurposeCodePicker
 * Can be used with fieldType.custom in FormCreatorComponent
 */
export function PurposeCodePickerFormField(props: customComponentInternalProps) {
	const { cField, handleChange } = props;
	const { value, customData, disabled } = cField.props;

	return (
		<PurposeCodePicker
			value={(value as number[]) || []}
			onChange={(newValue) => handleChange({ target: { value: newValue } })}
			options={customData?.options || []}
			loading={customData?.loading}
			noOptions={customData?.noOptions}
			disabled={disabled}
			hideTitle
		/>
	);
}
